import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { toast } from "sonner";
import { formatCurrency, formatDateTime, formatDate } from "@/lib/constants";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Receipt, Stethoscope, FileText } from "lucide-react";
import PatientAvatar from "@/components/crm/PatientAvatar";
import VisitedFields, { visitedPayload } from "@/components/crm/VisitedFields";
import {
  InvoiceLineItems, InvoiceTotals, InvoicePayment, invoiceRow, invoiceTotals,
  toInvoiceItems, emptyPayment, paymentBody, resolvePayment,
} from "@/components/crm/InvoiceLineItems";

/**
 * Reception closes the visit here: confirm the next visit, take the money,
 * raise the invoice. This is the step that moves the appointment to `visited` —
 * which is what every revenue report keys on, so a visit that never reaches this
 * screen never reaches revenue.
 */
export default function Checkout() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [nextVisitDate, setNextVisitDate] = useState("");
  const [nextVisitNotes, setNextVisitNotes] = useState("");
  const [money, setMoney] = useState({
    outcome: "converted", reason: "", revenue: "", outstanding: "",
    treatmentId: "", method: "cash",
  });
  const [note, setNote] = useState("");
  const [invoiceMode, setInvoiceMode] = useState("none");
  const [invoiceDesc, setInvoiceDesc] = useState("");
  // Full builder, Practo-style: the bill and its payment are made right here,
  // and the visit's revenue/outstanding come from them — typed once, not twice.
  const [rows, setRows] = useState([]);
  const [billDiscount, setBillDiscount] = useState(0);
  const [payment, setPayment] = useState(emptyPayment);
  // Held across a retry so a failed step 3 doesn't raise a second invoice.
  const [createdInvoice, setCreatedInvoice] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/appointments/${appointmentId}/checkout`)
      .then((r) => {
        const d = r.data;
        setData(d);
        setNextVisitDate(d.suggested_next_visit_date || "");
        setNextVisitNotes(d.consultation?.next_visit_notes || "");
        setInvoiceDesc(d.appointment?.service || "Consultation");
        if (d.appointment?.service) setRows([invoiceRow({ description: d.appointment.service })]);
        const open = (d.packages?.items || []).filter((i) => i.ledger && i.ledger.balance_due > 0);
        setMoney((m) => ({
          ...m,
          treatmentId: d.appointment?.treatment_id || (open.length === 1 ? open[0].id : ""),
          revenue: d.appointment?.revenue != null ? String(d.appointment.revenue) : "",
        }));
      })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); navigate("/appointments"); })
      .finally(() => setLoading(false));
  }, [appointmentId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" /></div>;
  if (!data) return null;

  const { appointment: appt, patient, consultation: consult, doctor_name } = data;
  const openPackages = (data.packages?.items || []).filter((i) => i.ledger && i.ledger.balance_due > 0);
  const selectedPkg = openPackages.find((p) => p.id === money.treatmentId);
  const alreadyVisited = appt.status === "visited";

  const elapsed = () => {
    if (!appt.checked_in_at) return "";
    const end = appt.checked_out_at ? new Date(appt.checked_out_at) : new Date();
    const mins = Math.max(0, Math.round((end - new Date(appt.checked_in_at)) / 60000));
    return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  const fullGrand = invoiceTotals(rows, billDiscount).grand;
  const fullPay = resolvePayment(payment, fullGrand);

  const submit = async () => {
    if (invoiceMode === "full" && !toInvoiceItems(rows).length) {
      toast.error("Add at least one treatment to the bill"); return;
    }
    setSaving(true);
    try {
      // 1. Next visit — a bare YYYY-MM-DD, straight from the date input. Never
      //    new Date(v).toISOString(): the recall builder wants the calendar date
      //    the patient agreed to, with no timezone shift.
      if (consult && (nextVisitDate !== (consult.next_visit_date || "").slice(0, 10)
                      || nextVisitNotes !== (consult.next_visit_notes || ""))) {
        await api.put(`/consultations/${consult.id}`, {
          next_visit_date: nextVisitDate || null,
          next_visit_notes: nextVisitNotes,
        });
      }

      // 2. Invoice, if reception wants one raised here. Skipped on a retry —
      //    createdInvoice remembers we already made it.
      let invoice = createdInvoice;
      if (invoiceMode === "quick" && !invoice) {
        const gross = parseFloat(money.revenue) || 0;
        const { data: inv } = await api.post("/invoices", {
          patient_id: appt.patient_id,
          branch_id: appt.branch_id,
          appointment_id: appt.id,
          items: [{ description: invoiceDesc || "Consultation", quantity: 1, rate: gross, tax_percent: 0 }],
          discount: 0,
        });
        invoice = inv;
        setCreatedInvoice(inv);
      }
      if (invoiceMode === "full" && !invoice) {
        const { data: inv } = await api.post("/invoices", {
          patient_id: appt.patient_id,
          branch_id: appt.branch_id,
          appointment_id: appt.id,
          items: toInvoiceItems(rows),
          discount: parseFloat(billDiscount) || 0,
          payment: paymentBody(payment, fullGrand),
        });
        invoice = inv;
        setCreatedInvoice(inv);
      }

      // 3. The money step, LAST because it is the only one that isn't safely
      //    repeatable. This single call does the package installment +
      //    record_payment, outstanding-as-a-flow, the lead converted/
      //    not-converted side-effects, close_recalls, history and audit.
      // Full builder: what the bill collected IS the visit's revenue (advance
      // included — that money was never on any visit's revenue until it is
      // spent on one), and what it left unpaid is the outstanding.
      const visitMoney = invoiceMode === "full"
        ? { ...money, treatmentId: "", revenue: String(invoice?.amount_paid ?? fullPay.collected),
            outstanding: String(invoice?.amount_due ?? fullPay.balance) }
        : money;
      await api.put(`/appointments/${appt.id}`, visitedPayload(visitMoney, { note }));

      if (invoice) {
        const pdf = `${api.defaults.baseURL}/invoices/${invoice.id}/pdf`;
        toast.success(`Checked out · invoice ${invoice.invoice_number}`, {
          duration: 10000,
          action: { label: (invoice.amount_paid || 0) > 0 ? "Print invoice & receipt" : "Print invoice",
                    onClick: () => window.open(pdf, "_blank") },
        });
      } else {
        toast.success("Checked out");
      }
      navigate("/appointments");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 pb-16">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/appointments")} className="h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5 text-navy" />
          <h2 className="font-display text-xl font-semibold text-navy">Checkout</h2>
        </div>
      </div>

      {alreadyVisited && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This visit has already been checked out. Re-submitting is blocked so the payment can't be taken twice.
        </p>
      )}
      {createdInvoice && (
        <p className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">
          Invoice {createdInvoice.invoice_number} already created — it won't be raised again on retry.
        </p>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Left: read-only context */}
        <div className="space-y-4">
          <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-4">
              <PatientAvatar
                patientId={appt.patient_id} name={appt.patient_name}
                photoPath={patient?.photo_path} updatedAt={patient?.photo_updated_at} size="lg"
              />
              <div className="min-w-0">
                <p className="font-display text-lg font-semibold text-navy">{appt.patient_name}</p>
                {patient?.patient_no && <p className="font-mono text-xs text-muted-foreground">{patient.patient_no}</p>}
                <p className="text-sm text-muted-foreground">
                  {[patient?.gender, patient?.age ? `${patient.age} yrs` : null, maskPhone(appt.mobile)].filter(Boolean).join(" · ")}
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-navy/10 pt-3 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Doctor</p>
                <p className="text-ink">{doctor_name || "—"}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Service</p>
                <p className="text-ink">{appt.service || "—"}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Visit</p>
                <p className="text-ink">{formatDateTime(appt.scheduled_at)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">In clinic</p>
                <p className="text-ink">{elapsed() || "—"}</p>
              </div>
            </div>
          </div>

          {/* Clinical summary — reception reads it; the server 403s any write. */}
          <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-navy" />
                <h3 className="font-display font-semibold text-navy">Consultation</h3>
              </div>
              {consult && (
                <Button size="sm" variant="outline" className="h-8 border-navy/20 text-navy"
                  onClick={() => navigate(`/consultation/${appt.id}`)}>
                  View
                </Button>
              )}
            </div>
            {!consult ? (
              <p className="text-sm text-muted-foreground">No consultation was recorded for this visit.</p>
            ) : (
              <div className="space-y-3 text-sm">
                {consult.diagnoses?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Diagnosis</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {consult.diagnoses.map((d, i) => (
                        <span key={i} className="rounded-full bg-navy/5 px-2 py-0.5 text-xs text-ink">
                          {d.condition}{d.is_primary ? " ★" : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {consult.prescription_items?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Prescription</p>
                    <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                      {consult.prescription_items.map((p, i) => (
                        <li key={i}>{p.drug_name} · {p.dosage} · {p.frequency} · {p.duration}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {consult.advice && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Advice</p>
                    <p className="text-xs text-muted-foreground">{consult.advice}</p>
                  </div>
                )}
                <Button size="sm" variant="outline" className="h-8 border-gold/30 text-gold hover:bg-gold/5"
                  onClick={() => window.open(`${api.defaults.baseURL}/consultations/${consult.id}/prescription-pdf`, "_blank")}>
                  <FileText className="mr-1.5 h-3.5 w-3.5" /> Print Rx
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right: the only editable column */}
        <div className="space-y-4">
          <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
            <h3 className="mb-3 font-display font-semibold text-navy">Next visit</h3>
            {consult?.next_visit_interval_label && (
              <p className="mb-2 inline-block rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-800">
                Doctor advised: {consult.next_visit_interval_label}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date">
                <Input type="date" value={nextVisitDate}
                  onChange={(e) => setNextVisitDate(e.target.value)} data-testid="checkout-next-visit" />
              </Field>
              <Field label="Notes">
                <Input value={nextVisitNotes} onChange={(e) => setNextVisitNotes(e.target.value)}
                  placeholder="What to prepare / bring" />
              </Field>
            </div>
            {nextVisitDate && (
              <button onClick={() => setNextVisitDate("")} className="mt-1 text-xs text-muted-foreground hover:underline">
                Clear
              </button>
            )}
          </div>

          <div className="space-y-4 rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
            <h3 className="font-display font-semibold text-navy">Outcome &amp; payment</h3>
            <VisitedFields value={money} onChange={setMoney} packages={openPackages}
                           hideMoney={invoiceMode === "full"} />
            {invoiceMode === "full" && (
              <p className="text-xs text-muted-foreground">
                Revenue and outstanding come from the invoice below.
              </p>
            )}
            <Field label="Remark (optional)">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note / remark" />
            </Field>
          </div>

          <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
            <h3 className="mb-3 font-display font-semibold text-navy">Invoice</h3>
            <div className="space-y-2">
              {[
                { value: "none", label: selectedPkg ? "None — payment goes on the package" : "None" },
                { value: "quick", label: "Quick invoice for this visit" },
                { value: "full", label: "Full invoice builder" },
              ].map((opt) => (
                <label key={opt.value} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="radio" name="invoiceMode" value={opt.value}
                    checked={invoiceMode === opt.value}
                    onChange={() => setInvoiceMode(opt.value)}
                    disabled={opt.value !== "none" && !!selectedPkg}
                    data-testid={`checkout-invoice-${opt.value}`}
                  />
                  <span className={opt.value !== "none" && selectedPkg ? "text-muted-foreground" : "text-ink"}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
            {selectedPkg && (
              <p className="mt-2 text-xs text-muted-foreground">
                The package installment and payment are raised automatically — a second invoice here would bill the same money twice.
              </p>
            )}
            {invoiceMode === "quick" && (
              <Field label="Description">
                <Input value={invoiceDesc} onChange={(e) => setInvoiceDesc(e.target.value)} />
              </Field>
            )}
            {invoiceMode === "full" && (
              <div className="mt-3 space-y-3" data-testid="checkout-full-invoice">
                <InvoiceLineItems rows={rows} onChange={setRows} />
                <InvoiceTotals rows={rows} discount={billDiscount} onDiscount={setBillDiscount} />
                {fullGrand > 0 && (
                  <InvoicePayment patientId={appt.patient_id} grand={fullGrand}
                                  value={payment} onChange={setPayment} />
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-xl border border-navy/10 bg-cream/40 px-5 py-4">
            <div className="text-sm">
              <p className="text-muted-foreground">Collecting today</p>
              <p className="font-display text-xl font-semibold text-navy">
                {formatCurrency(invoiceMode === "full" ? fullPay.collected : (parseFloat(money.revenue) || 0))}
              </p>
            </div>
            <Button
              onClick={submit}
              disabled={saving || alreadyVisited}
              className="bg-navy text-white hover:bg-navy-hover"
              data-testid="checkout-complete"
            >
              {saving ? "Saving…" : invoiceMode === "full" ? "Accept Payment & Complete Visit" : "Complete Visit"}
            </Button>
          </div>
          {consult?.next_visit_date && (
            <p className="text-center text-xs text-muted-foreground">
              Currently advised: {formatDate(consult.next_visit_date)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
